/**
 * SectionRoles — los roles conceptuales del producto futuro.
 *
 * Buenas plantillas primero, personalización después: aquí no hay un muro de
 * casillas. Cada rol es una ficha con una frase que explica qué hace esa
 * persona; el detalle de permisos vive detrás de «Ver permisos» y la
 * comparación completa detrás de «Comparar todos los roles».
 *
 * Dos decisiones que no son obvias:
 * 1. Los roles no se editan en la vista previa, así que esta sección lee el
 *    catálogo estático del dataset y no escribe en el store. No hay guardado
 *    ni botón de guardar porque no hay nada que guardar.
 * 2. La palabra «propietario» significa siempre quien es titular de una o
 *    varias unidades. Quien administra el negocio es un administrador, aunque
 *    a veces sea la misma persona. Las fichas primera y última lo dicen en voz
 *    alta para que la diferencia se vea sin leer la tabla.
 */
import React, { useState } from "react";
import {
  ShieldCheck, UsersThree, ChatCircle, Broom, Wrench, Receipt, House, CaretRight,
} from "@phosphor-icons/react";
import Chip from "@/components/ui-dv/Chip";
import SidePanel from "@/components/ui-dv/SidePanel";
import {
  Block, Card, ReadRow, Note, Advanced,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import {
  ROLE_TEMPLATES, PERMISSION_AREAS, PERMISSION_LEVEL_LABEL,
} from "@/data/settingsV2Demo";

/** Un icono por rol, sin color: solo ayuda a distinguir las fichas de un vistazo. */
const ROLE_ICONS = {
  property_admin: ShieldCheck,
  manager: UsersThree,
  reception: ChatCircle,
  housekeeping: Broom,
  maintenance: Wrench,
  finance: Receipt,
  unit_owner: House,
};

/** Aclaración corta de cada nivel, en las palabras del cliente. */
const LEVEL_HINT = {
  none: "No aparece en su menú",
  read: "Puede consultar, no modificar",
  write: "Puede consultar y modificar",
  full: "Incluye configurar y aprobar",
};

/** Frase que sitúa a los dos extremos del listado sin usar palabras ambiguas. */
const ROLE_BADGE = {
  property_admin: "Administra el negocio",
  unit_owner: "Es titular de unidades",
};

const LEVEL_TEXT = {
  none: "text-stone-300 dark:text-stone-600",
  read: "text-stone-500 dark:text-stone-400",
  write: "text-stone-700 dark:text-stone-300",
  full: "text-stone-900 dark:text-stone-100 font-medium",
};

function areasWithAccess(role) {
  return PERMISSION_AREAS.filter((a) => (role.permissions[a.key] || "none") !== "none").length;
}

export default function SectionRoles() {
  const [openRole, setOpenRole] = useState(null);

  return (
    <div data-testid="section-roles">
      <Block
        title="Plantillas de rol"
        description="Qué ve y qué puede hacer cada persona del equipo, según su trabajo."
        testId="roles-templates"
      >
        <Note testId="roles-scope-note">
          Estos son los roles del producto futuro. Sirven para enseñar cómo se repartirá el
          trabajo cuando el equipo completo entre en la plataforma. No cambian cómo se entra
          hoy ni qué permisos tiene nadie ahora mismo: en la vista previa se muestran, no se
          aplican ni se editan.
        </Note>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="roles-grid">
          {ROLE_TEMPLATES.map((role) => (
            <RoleCard key={role.role_key} role={role} onOpen={() => setOpenRole(role)} />
          ))}
        </div>

        <div
          className="mt-4 text-xs text-stone-500 dark:text-stone-400 max-w-2xl"
          data-testid="roles-naming-note"
        >
          En este producto «propietario» quiere decir siempre una cosa: quien es titular de una
          o varias unidades y ve solo lo que pasa en las suyas. Quien administra el negocio de
          alojamiento es un administrador, aunque sea la misma persona.
        </div>

        <Advanced label="Comparar todos los roles" testId="roles-compare">
          <RoleMatrix />
        </Advanced>
      </Block>

      <SidePanel
        open={!!openRole}
        onClose={() => setOpenRole(null)}
        title={openRole?.label || "Rol"}
        subtitle="Plantilla de rol"
        testId="roles-panel"
        footer={
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => setOpenRole(null)}
              className="dv-btn dv-btn-ghost text-sm min-h-[44px]"
              data-testid="roles-panel-dismiss"
            >
              Cerrar
            </button>
          </div>
        }
      >
        {openRole && <RoleDetail role={openRole} />}
      </SidePanel>
    </div>
  );
}

function RoleCard({ role, onOpen }) {
  const Icon = ROLE_ICONS[role.role_key] || UsersThree;
  const badge = ROLE_BADGE[role.role_key];
  const count = areasWithAccess(role);

  return (
    <div className="dv-card flex flex-col" data-testid={`roles-card-${role.role_key}`}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
          <Icon size={18} className="text-stone-600 dark:text-stone-300" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 dark:text-stone-100">{role.label}</div>
          {badge && (
            <div className="mt-1">
              <Chip tone="stone" size="xs">{badge}</Chip>
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-stone-500 dark:text-stone-400 mt-3 flex-1">{role.summary}</p>

      <div className="mt-4 pt-3 border-t border-stone-100 dark:border-stone-800 flex items-center justify-between gap-2">
        <span className="text-[11px] text-stone-400 dark:text-stone-500 tabular-nums">
          Entra en {count} de {PERMISSION_AREAS.length} áreas
        </span>
        <button
          type="button"
          onClick={onOpen}
          data-testid={`role-permissions-${role.role_key}`}
          className="dv-btn dv-btn-ghost text-xs min-h-[44px] inline-flex items-center gap-1"
        >
          Ver permisos
          <CaretRight size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function RoleDetail({ role }) {
  return (
    <div className="space-y-5" data-testid={`roles-detail-${role.role_key}`}>
      <p className="text-sm text-stone-600 dark:text-stone-300">{role.summary}</p>

      <div>
        <div className="text-[11px] tracking-[0.18em] uppercase text-stone-500 mb-2">
          Acceso por área
        </div>
        <div>
          {PERMISSION_AREAS.map((area) => {
            const level = role.permissions[area.key] || "none";
            return (
              <ReadRow
                key={area.key}
                label={area.label}
                hint={LEVEL_HINT[level]}
                value={PERMISSION_LEVEL_LABEL[level]}
                testId={`roles-panel-area-${area.key}`}
              />
            );
          })}
        </div>
      </div>

      <p className="text-xs text-stone-500 dark:text-stone-400" data-testid="roles-panel-note">
        Un área sin acceso no se le muestra a esa persona: no aparece en su menú ni en sus
        avisos. Es una vista del producto futuro, todavía no se aplica.
      </p>
    </div>
  );
}

function RoleMatrix() {
  return (
    <>
      <Card padded={false} testId="roles-matrix">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm" data-testid="roles-matrix-table">
            <thead>
              <tr className="border-b border-stone-100 dark:border-stone-800">
                <th className="sticky left-0 bg-white dark:bg-stone-900 text-left font-normal text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400 px-4 py-3">
                  Área
                </th>
                {ROLE_TEMPLATES.map((role) => (
                  <th
                    key={role.role_key}
                    className="text-left font-normal text-[11px] text-stone-500 dark:text-stone-400 px-3 py-3 whitespace-nowrap"
                    data-testid={`roles-matrix-col-${role.role_key}`}
                  >
                    {role.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSION_AREAS.map((area) => (
                <tr
                  key={area.key}
                  className="border-b border-stone-100 dark:border-stone-800 last:border-0"
                  data-testid={`roles-matrix-row-${area.key}`}
                >
                  <th
                    scope="row"
                    className="sticky left-0 bg-white dark:bg-stone-900 text-left font-normal text-stone-700 dark:text-stone-300 px-4 py-3 whitespace-nowrap"
                  >
                    {area.label}
                  </th>
                  {ROLE_TEMPLATES.map((role) => {
                    const level = role.permissions[area.key] || "none";
                    return (
                      <td
                        key={role.role_key}
                        className={`px-3 py-3 whitespace-nowrap ${LEVEL_TEXT[level]}`}
                        data-testid={`roles-matrix-cell-${area.key}-${role.role_key}`}
                      >
                        {PERMISSION_LEVEL_LABEL[level]}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div
        className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-stone-500 dark:text-stone-400"
        data-testid="roles-matrix-legend"
      >
        <span>Ver: consulta</span>
        <span>Editar: consulta y modifica</span>
        <span>Total: incluye configurar y aprobar</span>
        <span>—: sin acceso</span>
      </div>
    </>
  );
}
